import * as React from 'react';
import {useMachine} from '@xstate/react';
import Echo from 'laravel-echo';
import {Logger} from '@practicegenius/domain/src/common/logger';
import {ApiClient} from '@practicegenius/http-clients-js/src/types';
import * as registration from './machines/accountRegistration';
import * as ScreenContainers from './screens';
import {Navigator} from './data/navigator';
import {AlreadyRegisteredScreenType} from './screens/AlreadyRegistered';
import {LinkPracticeScreenType} from './screens/LinkPractice';
import {NoGenericRegistrationScreenType} from './screens/NoGenericRegistration';
import {RegistrationMethodsScreenType} from './screens/RegistrationMethods';
import {RegisterNamesScreenType} from './screens/RegisterNames';
import {NameMismatchScreenType} from './screens/NameMismatch';
import {RegisterBirthdayScreenType} from './screens/RegisterBirthday';
import {RegisterEmailScreenType} from './screens/RegisterEmail';
import {ConfirmEmailScreenType} from './screens/ConfirmEmail';
import {EmailConfirmedScreenType} from './screens/EmailConfirmed';
import {RegisterUsernameScreenType} from './screens/RegisterUsername';
import {RegisterPasswordScreenType} from './screens/RegisterPassword';
import {RegistrationSuccessScreenType} from './screens/RegistrationSuccess';
import {SocialAlreadyRegisteredScreenType} from './screens/SocialAlreadyRegistered';
import {Context} from './machines/machineTypes';
import {UseMachineType} from './machines/useAccountRegistrationMachineType';
import {TellUsMoreScreenType} from './screens/TellUsMore';
import {RegisterPhoneNumberScreenType} from './screens/RegisterPhoneNumber';
import {LinkInviteScreenType} from './screens/LinkInvite';
import {LinkBusinessCodeScreenType} from './screens/LinkBusinessCode';

type AppProps = {
  logger: Logger;
  navigator: Navigator;
  coreApiClient: ApiClient;
  sitesDotClient: ApiClient;
  // A hook that listens to global navigation events from the navigator
  // and fires the corresponding events into the machine
  useRouter: (nav: Navigator, send: UseMachineType[1]) => unknown;
  // This is a component that wraps routes that should be guarded against
  // - Direct Navigation
  // - Prematurely navigating away
  RouteProtection?: React.ComponentType<{
    registrationMachine: UseMachineType;
    children?: React.ReactNode;
  }>;

  screens: {
    AlreadyRegistered: AlreadyRegisteredScreenType;
    LinkPractice: LinkPracticeScreenType;
    LinkInvite: LinkInviteScreenType;
    LinkBusinessCode: LinkBusinessCodeScreenType;
    TellUsMore: TellUsMoreScreenType;
    NoGenericRegistration: NoGenericRegistrationScreenType;
    RegistrationMethods: RegistrationMethodsScreenType;
    RegisterNames: RegisterNamesScreenType;
    NameMismatch: NameMismatchScreenType;
    RegisterBirthday: RegisterBirthdayScreenType;
    RegisterPhoneNumber: RegisterPhoneNumberScreenType;
    RegisterEmail: RegisterEmailScreenType;
    ConfirmEmail: ConfirmEmailScreenType;
    EmailConfirmed: EmailConfirmedScreenType;
    RegisterUsername: RegisterUsernameScreenType;
    RegisterPassword: RegisterPasswordScreenType;
    RegistrationSuccess: RegistrationSuccessScreenType;
    SocialAlreadyRegistered: SocialAlreadyRegisteredScreenType;
  };

  containerProps: {
    isGenericHub: boolean;
    pgCoreUrl: string;
    getWebsocket: () => Echo;
    showAlert: Context['showAlert'];
    redirect: Context['redirect'];
  };
};

export function RegisterAccountAppShell({
  logger,
  containerProps: {isGenericHub, pgCoreUrl, getWebsocket, showAlert, redirect},
  screens,
  navigator,
  coreApiClient,
  useRouter,
  sitesDotClient,
  RouteProtection = React.Fragment,
}: AppProps) {
  const machineMemo = React.useMemo(
    () =>
      registration.createRegistrationMachine({
        logger,
        navigator,
        apiClient: coreApiClient,
        sitesDotClient,
        showAlert,
        redirect,
      }),
    [logger, navigator, coreApiClient, sitesDotClient, showAlert, redirect]
  );

  // @ts-ignore
  const registrationMachine = useMachine(machineMemo, {
    devTools: process.env.NODE_ENV === 'development',
  }) as UseMachineType;

  const [{matches, context}, send] = registrationMachine;

  useRouter(context.navigator, send);

  return (
    <>
      {/* These are standard routes */}
      {matches(registration.RegistrationState.linkPractice) && (
        <ScreenContainers.LinkPractice
          registrationMachine={registrationMachine}
          isGenericHub={isGenericHub}
          children={screens.LinkPractice}
        />
      )}

      {matches(registration.RegistrationState.processingInviteCode) && (
        <ScreenContainers.LinkInvite children={screens.LinkInvite} />
      )}

      {matches(registration.RegistrationState.processingBusinessCode) && (
        <ScreenContainers.LinkBusinessCode
          children={screens.LinkBusinessCode}
        />
      )}

      {matches(registration.RegistrationState.noGenericRegistration) && (
        <ScreenContainers.NoGenericRegistration
          children={screens.NoGenericRegistration}
        />
      )}

      {matches(registration.RegistrationState.tellUsMore) && (
        <RouteProtection registrationMachine={registrationMachine}>
          <ScreenContainers.TellUsMore
            registrationMachine={registrationMachine}
            children={screens.TellUsMore}
          />
        </RouteProtection>
      )}

      {matches(registration.RegistrationState.nameMismatch) && (
        <ScreenContainers.NameMismatch
          registrationMachine={registrationMachine}
          children={screens.NameMismatch}
        />
      )}

      {matches(registration.RegistrationState.alreadyRegistered) && (
        <ScreenContainers.AlreadyRegistered
          registrationMachine={registrationMachine}
          children={screens.AlreadyRegistered}
        />
      )}

      {matches(registration.RegistrationState.alreadyRegisteredSocially) && (
        <ScreenContainers.SocialAlreadyRegistered
          registrationMachine={registrationMachine}
          children={screens.SocialAlreadyRegistered}
        />
      )}

      {/* These routes protect from users directly navigating to them (since the registration state would be totally empty by navigating here) */}
      {/* They also warn a user against leaving since it will obliterate their registration process */}
      {matches(registration.RegistrationState.registrationMethods) && (
        <RouteProtection registrationMachine={registrationMachine}>
          <ScreenContainers.RegistrationMethods
            registrationMachine={registrationMachine}
            pgCoreUrl={pgCoreUrl}
            children={screens.RegistrationMethods}
          />
        </RouteProtection>
      )}

      {matches(registration.RegistrationState.nameVerification) && (
        <RouteProtection registrationMachine={registrationMachine}>
          <ScreenContainers.RegisterNames
            registrationMachine={registrationMachine}
            children={screens.RegisterNames}
          />
        </RouteProtection>
      )}

      {matches(registration.RegistrationState.birthdate) && (
        <RouteProtection registrationMachine={registrationMachine}>
          <ScreenContainers.RegisterBirthday
            registrationMachine={registrationMachine}
            children={screens.RegisterBirthday}
          />
        </RouteProtection>
      )}

      {matches(registration.RegistrationState.phoneNumber) && (
        <RouteProtection registrationMachine={registrationMachine}>
          <ScreenContainers.RegisterPhoneNumber
            registrationMachine={registrationMachine}
            children={screens.RegisterPhoneNumber}
          />
        </RouteProtection>
      )}

      {matches(registration.RegistrationState.email) && (
        <RouteProtection registrationMachine={registrationMachine}>
          <ScreenContainers.RegisterEmail
            registrationMachine={registrationMachine}
            children={screens.RegisterEmail}
          />
        </RouteProtection>
      )}

      {matches(registration.RegistrationState.emailVerification) && (
        <RouteProtection registrationMachine={registrationMachine}>
          <ScreenContainers.ConfirmEmail
            registrationMachine={registrationMachine}
            getWebsocket={getWebsocket}
            children={screens.ConfirmEmail}
          />
        </RouteProtection>
      )}

      {matches(registration.RegistrationState.emailVerified) && (
        <RouteProtection registrationMachine={registrationMachine}>
          <ScreenContainers.EmailConfirmed
            registrationMachine={registrationMachine}
            children={screens.EmailConfirmed}
          />
        </RouteProtection>
      )}

      {matches(registration.RegistrationState.username) && (
        <RouteProtection registrationMachine={registrationMachine}>
          <ScreenContainers.RegisterUsername
            registrationMachine={registrationMachine}
            children={screens.RegisterUsername}
          />
        </RouteProtection>
      )}

      {matches(registration.RegistrationState.password) && (
        <RouteProtection registrationMachine={registrationMachine}>
          <ScreenContainers.RegisterPassword
            registrationMachine={registrationMachine}
            children={screens.RegisterPassword}
          />
        </RouteProtection>
      )}

      {matches(registration.RegistrationState.success) && (
        <RouteProtection registrationMachine={registrationMachine}>
          <ScreenContainers.RegistrationSuccess
            registrationMachine={registrationMachine}
            children={screens.RegistrationSuccess}
          />
        </RouteProtection>
      )}
    </>
  );
}
